import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Stack, Typography } from '@mui/material';
import type { BillingInterval, Plan, PlanDuration } from '@weft/types';
import { BILLING_INTERVALS, DEFAULT_CURRENCY, PlanInputSchema } from '@weft/types';
import { UNLIMITED_DURATION } from '@weft/types';
import { Price } from 'apps/app/src/components/design/Price';
import { StatusInfoCard } from 'apps/app/src/components/design/status-info-card/StatusInfoCard';
import { VyPageLayout } from 'apps/app/src/components/new-design/layout/VyPageLayout';
import React from 'react';
import { Helmet } from 'react-helmet';
import { FormProvider, useForm } from 'react-hook-form';
import { useQueries } from 'react-query';
import { NavLink } from 'react-router-dom';
import { withSuspenseBoundary } from '../../../components/utility/SuspenseBoundary';
import { usePlanById, usePlanByIdOptions } from '../../../hooks/requests/use-plan-by-id';
import { usePlanOverview, usePlanOverviewOptions } from '../../../hooks/requests/use-plan-overview';
import { useValidatedParams } from '../../../hooks/use-validated-params';
import { ContractNavbar } from '../../contract/contract-page-components/ContractNavbar';
import { ContractPageTitle } from '../../contract/contract-page-components/ContractPageTitle';
import { PlanCharges } from './plan-view-page-components/PlanCharges';

const PlanViewPage: React.FC = () => {
  const { planId } = useValidatedParams<['planId']>('planId');

  const { data: { plan: planOverview } } = usePlanOverview(planId);
  const { data: { plan } } = usePlanById(planId);

  const methods = useForm<Plan>({
    resolver: zodResolver(PlanInputSchema),
    defaultValues: plan,
  });

  return (
    <>
      <Helmet>
        <title>{`Plan ${plan.name}`}</title>
      </Helmet>

      <VyPageLayout
        path={['Plans', <NavLink to="/plans">Plans and Pricing</NavLink>, plan.name]}
        title={<ContractPageTitle plan={plan} />}
        action={
          <>
            <Box sx={{ padding: '20px' }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '16px',
                  alignItems: 'center',
                }}
              >
                <ContractNavbar
                  contractId={plan.id}
                  plan={plan}
                  description={plan.description}
                  status={plan.status}
                />
              </Box>
            </Box>
          </>
        }
      >
        <FormProvider {...methods}>
          <Box sx={{ flexGrow: 1, backgroundColor: '#F5F5F5' }}>
            <Box sx={{ marginX: 'auto', maxWidth: '1280px' }}>
              <Stack sx={{ '.MuiStack-root': { gap: 2 } }}>
                <Box
                  sx={{
                    borderColor: 'line',
                    borderRadius: 1,
                    backgroundColor: 'white',
                    border: 'none',
                    padding: 3,
                    boxShadow: '0px 10px 24px 0px rgba(0,0,0,0.03)',
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography
                      fontSize="16px"
                      fontWeight={400}
                      color="rgba(27,61,85,0.60)"
                    >
                      Revenue
                    </Typography>
                    <Price
                      sx={{ fontSize: '34px', fontWeight: '300' }}
                      currency={DEFAULT_CURRENCY}
                    >
                      {planOverview.revenue}
                    </Price>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, paddingTop: 8 }}>
                    <StatusInfoCard
                      title="Billing Interval"
                    >
                      {getBillingInterval(plan.billingData.billingInterval)}
                    </StatusInfoCard>
                    <StatusInfoCard
                      title="Billing Duration"
                    >      
                      {getBillingDuration(plan.billingData.duration)}
                    </StatusInfoCard>
                    <StatusInfoCard title="Customers">       
                      {planOverview.customersCount.toString()}
                    </StatusInfoCard>
                  </Box>
                </Box>
              </Stack>
              <PlanCharges
                plan={plan}
                currency={DEFAULT_CURRENCY}
              />
            </Box>
          </Box>
        </FormProvider>
      </VyPageLayout>
    </>
  );
};

const withPlanViewPageParallelQueries = <ComponentProps extends Record<string, unknown>>(
  Component: React.ComponentType<ComponentProps>,
) => {
  return (componentProps: ComponentProps) => {
    const { planId } = useValidatedParams<['planId']>('planId');

    const planOverviewOptions = usePlanOverviewOptions(planId);
    const planByIdOptions = usePlanByIdOptions(planId);

    useQueries([planOverviewOptions, planByIdOptions]);

    return <Component {...componentProps} />;
  };
};

const PlanViewPageWithSuspenseBoundary = withPlanViewPageParallelQueries(
  withSuspenseBoundary(PlanViewPage),
);
export { PlanViewPageWithSuspenseBoundary as PlanViewPage };

function getBillingDuration(duration: PlanDuration) {
  switch (duration) {
    case 1:
      return '1 month';
    case UNLIMITED_DURATION:
      return 'Unlimited';
    default:
      return `${duration} months`;
  }
}

function getBillingInterval(billingInterval: BillingInterval) {
  switch (billingInterval) {
    case BILLING_INTERVALS.SixMonths:
      return '6 months';
    case BILLING_INTERVALS.BiMonthly:
      return 'Bi monthly';
    default:
      return billingInterval;
  }
}
